/**
 * One way to talk to the API.
 *
 * Every call goes through `request`, so the two things that must never be
 * forgotten — sending the session cookie, and turning a failure into an error
 * with the server's own words in it — happen once rather than at each call
 * site.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** The server's `detail`, which is written to be shown to a person. */
    readonly detail: string,
  ) {
    super(detail || `HTTP ${status}`)
    this.name = 'ApiError'
  }

  /** Not signed in. The caller decides whether that is expected. */
  get isUnauthenticated() {
    return this.status === 401
  }

  /** Signed in as the wrong person — recoverable by signing out. */
  get isWrongAccount() {
    return this.status === 403
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(path, {
    // Without this the session cookie is not sent and every authenticated
    // call 401s, which looks exactly like being signed out.
    credentials: 'same-origin',
    headers:
      init.body === undefined
        ? init.headers
        : { 'content-type': 'application/json', ...init.headers },
    ...init,
  })

  if (!response.ok) {
    // FastAPI puts the human-readable reason in `detail`. Losing it and
    // showing "Request failed" instead is how a fixable problem — the wrong
    // account, an address already in use — becomes a support ticket.
    let detail = ''
    try {
      const body = await response.json()
      detail = typeof body?.detail === 'string' ? body.detail : ''
    } catch {
      detail = ''
    }
    throw new ApiError(response.status, detail)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

const body = (data: unknown) => ({ body: JSON.stringify(data) })

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', ...(data === undefined ? {} : body(data)) }),
  patch: <T>(path: string, data: unknown) =>
    request<T>(path, { method: 'PATCH', ...body(data) }),
  put: <T>(path: string, data: unknown) =>
    request<T>(path, { method: 'PUT', ...body(data) }),
  // `del`, not `delete` — the word is reserved and cannot be a bare method
  // name in every position this object is used from.
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/** Query strings, without the `?q=undefined` class of bug. */
export function qs(params: Record<string, string | number | undefined>) {
  const pairs = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== '',
  )
  return pairs.length
    ? `?${new URLSearchParams(pairs.map(([k, v]) => [k, String(v)]))}`
    : ''
}
